/**
 * The money side of changing a voucher on an existing order.
 *
 * Lives outside the form so it can be tested without mounting React: everything here
 * decides which way cash moves, and that is the part worth a test.
 */

import type { SettlementPreset } from "./settlement-presets";

export type { SettlementPreset as VoucherPreset } from "./settlement-presets";
/**
 * The preset table, the two direction lists and the risky set live in
 * `settlement-presets.ts`: change-package settles through the same backend matrix, and two
 * copies of one table drift. Re-exported under the names this module has always used so a
 * caller that only deals with vouchers still reads as if it does.
 */
export {
	CHARGE_PRESETS,
	leavesPaymentOpen as leavesVoucherPaymentOpen,
	presetsFor,
	REFUND_PRESETS,
	RISKY_PRESETS as RISKY_VOUCHER_PRESETS,
	SETTLEMENT_PRESETS as VOUCHER_PRESETS,
} from "./settlement-presets";

export interface VoucherOption {
	id: string;
	code: string;
	name: string | null;
	/** PER_PACKAGE is an RKI (Bonus RO) voucher: its discountValue is only a label */
	discountType: "PERCENTAGE" | "FIXED" | "PER_PACKAGE";
	discountValue: number;
	/**
	 * PER_PACKAGE only: the discount of the order's package rule on the day the order was
	 * created, as the backend judges it. null = none, so the backend refuses the code;
	 * absent = the rules could not be read.
	 */
	packageDiscount?: number | null;
	maxDiscountAmount: number | null;
	minOrderAmount: number | null;
	remainingQuota: number;
	validUntil: string | null;
	/**
	 * Rails' own `Voucher#can_be_used?`. False means quota or dates block it — the row
	 * still appears in the picker, because the overrides that honour it anyway live in
	 * the form.
	 */
	canBeUsed: boolean;
	isCurrent: boolean;
}

/**
 * What a voucher would knock off this subtotal, by the same rules the Go side applies:
 * a percentage capped by `max_discount_amount`, a fixed amount taken flat, never below
 * zero and never more than the subtotal.
 *
 * This ONLY decides which preset list to show. It is never displayed as money and never
 * sent: the backend re-judges every code against the order's current package and
 * subtotal, and its answer is the one that moves cash.
 */
export function estimateDiscount(
	voucher: VoucherOption,
	subtotal: number,
): number {
	const raw =
		voucher.discountType === "PER_PACKAGE"
			? (voucher.packageDiscount ?? 0)
			: voucher.discountType === "PERCENTAGE"
				? (subtotal * voucher.discountValue) / 100
				: voucher.discountValue;
	const capped =
		voucher.maxDiscountAmount != null
			? Math.min(raw, voucher.maxDiscountAmount)
			: raw;
	return Math.max(0, Math.min(capped, subtotal));
}

/**
 * How much the order total moves, positive when it goes UP.
 *
 * Returns null when no direction can be stated honestly — an apply with nothing picked
 * yet. Callers must not coerce that to zero: "no answer" and "no change" pick different
 * presets.
 */
export function estimateTotalDelta({
	action,
	voucher,
	currentDiscount,
	subtotal,
}: {
	action: "APPLY" | "REMOVE";
	voucher: VoucherOption | null;
	currentDiscount: number;
	subtotal: number;
}): number | null {
	// Removing the discount always raises the total by exactly what was being taken off.
	if (action === "REMOVE") return currentDiscount;
	if (!voucher) return null;
	return currentDiscount - estimateDiscount(voucher, subtotal);
}

/**
 * Re-picking the voucher an order already carries is the supported way to recompute a
 * percentage discount after a package change — the backend answers `revalidated`. But it
 * is only worth doing when the stored discount has actually drifted from what the voucher
 * would compute today; otherwise it is a no-op that bills nothing and changes nothing.
 *
 * Money is carried as a float, so this compares with a sub-rupiah tolerance rather than
 * `===`: 0.1 + 0.2 problems must not make an in-sync voucher look stale.
 */
export function isRevalidateNoOp(
	voucher: VoucherOption,
	subtotal: number,
	currentDiscount: number,
): boolean {
	if (!voucher.isCurrent) return false;
	return Math.abs(estimateDiscount(voucher, subtotal) - currentDiscount) < 0.5;
}

/**
 * Whether the order's subtotal falls short of what the voucher demands.
 *
 * Unlike an exhausted or expired voucher, this one has no override — the Go side is
 * explicit that quota and expiry are inventory, while the minimum order and package
 * eligibility are pricing correctness and are never waived (`vouchercalc.go`). So a row
 * that fails this can only ever be refused, and the picker closes it rather than
 * spending a round trip to be told so.
 *
 * Judged against the subtotal, matching `vouchercalc.go`: "never the post-discount
 * total". Comparing against the total would open rows the backend then refuses.
 */
export function isBelowMinOrder(
	voucher: VoucherOption,
	subtotal: number,
): boolean {
	return voucher.minOrderAmount != null && subtotal < voucher.minOrderAmount;
}

/**
 * An RKI code on a package whose rule has no patient discount. Like a missed minimum
 * order this is pricing, which no override waives (`vouchercalc.go`), so the picker
 * closes the row instead of letting the backend refuse it.
 */
export function isPackageIneligible(voucher: VoucherOption): boolean {
	return (
		voucher.discountType === "PER_PACKAGE" && voucher.packageDiscount === null
	);
}

/** What the dialog collects. Mirrors the form's field names, which are the wire names. */
export interface VoucherFormValues {
	action: "APPLY" | "REMOVE";
	voucher_code?: string;
	preset: SettlementPreset;
	expiry_minutes?: string;
	reason?: string;
	ignore_quota?: boolean;
	ignore_expiry?: boolean;
	/**
	 * Not a field on the form. Armed by the retry button that appears after the backend
	 * refuses a cancelled order, and merged over the form's values on the way here.
	 */
	reactivate_if_cancelled?: boolean;
}

/**
 * The one assembly of what the dialog sends to Rails.
 *
 * Both the request and the "inspect payload" disclosure read this. They used to build
 * the object separately and drifted exactly as you would expect: the preview showed a
 * number where the request sent a string, and left out the overrides and the reason
 * altogether. A receipt that quietly disagrees with the request is worse than no
 * receipt, so there is only one of them now.
 */
export function buildVoucherFormData(
	values: VoucherFormValues,
): Record<string, string | boolean> {
	const applying = values.action === "APPLY";

	const data: Record<string, string | boolean> = {
		action: values.action,
		// Deliberately absent on REMOVE. The backend ignores it there, but sending the
		// code an admin happens to have selected would make the request read like an
		// apply to anyone reading it back.
		...(applying && values.voucher_code
			? { voucher_code: values.voucher_code }
			: {}),
		// The settlement itself. Rails derives the flags from this name — they are not
		// accepted on the wire — and echoes it back in the error bag so a dialog
		// unmounted by a failure can restore which preset the admin had picked.
		preset: values.preset,
	};

	// Only the gateway branch issues a link, so only it carries a lifetime.
	if (values.preset === "gateway" && values.expiry_minutes) {
		data.expiry_minutes = values.expiry_minutes;
	}

	// Sent only when ticked. The backend defaults all three to false, so an absent key
	// and an explicit false mean the same thing, and an absent one cannot be misread as
	// a decision somebody made.
	if (values.ignore_quota) data.ignore_quota = true;
	if (values.ignore_expiry) data.ignore_expiry = true;
	if (values.reactivate_if_cancelled) data.reactivate_if_cancelled = true;

	const reason = values.reason?.trim();
	if (reason) data.reason = reason;

	return data;
}
